import { useEffect, useState, type ReactNode } from 'react'
import {
  Avatar, Box, Button, CloseButton, Dialog, Drawer, HStack, Input, Portal, Spinner, Text, VStack,
} from '@chakra-ui/react'
import { useQueryClient } from '@tanstack/react-query'
import { toaster } from '@/components/ui/toaster'
import { ResponsiveModal } from '../../../shared'
import { useIsMobile } from '../../../../hooks/useIsMobile'
import { useItineraryWeek, useSaveItineraryWeek } from '../../../../hooks/useItinerary'
import { useAvailableCanvassers } from '../../../../hooks/useCampaign'
import { extractApiMessage } from '../../../../lib/apiError'
import { STATIC_SENTINEL_KEY } from '../../itinerary/StaticCellDropZone'
import { sameDayHeadsUp } from '../../itinerary/doubleBooking'
import { mondayOf, toLocalDateStr } from './routeDates'
import type { FollowUpRouteDto } from '../../../../types/followUp'

interface AssignRouteSheetProps {
  isOpen: boolean
  onClose: () => void
  campaignId: string
  route: FollowUpRouteDto | null
  /** yyyy-MM-dd the date input starts on (default today). */
  initialDate?: string
}

/** Assigns one follow-up route to a canvasser on a day through the EXISTING itinerary week save
 *  (one upsert, no new endpoint). Bottom sheet on phone, modal on desktop. */
export function AssignRouteSheet({ isOpen, onClose, campaignId, route, initialDate }: AssignRouteSheetProps) {
  const isMobile = useIsMobile()
  const queryClient = useQueryClient()
  const [date, setDate] = useState(() => initialDate ?? toLocalDateStr(new Date()))
  const [grantId, setGrantId] = useState<string | null>(null)

  // Each open starts fresh: the initial (or today's) date and no canvasser picked.
  useEffect(() => {
    if (!isOpen) return
    setGrantId(null)
    setDate(initialDate ?? toLocalDateStr(new Date()))
  }, [isOpen, route?.turfId, initialDate])

  const { data: week } = useItineraryWeek(campaignId, mondayOf(date))
  const isStatic = week?.assignmentMode === 'Static'
  const scheduledDate = isStatic ? STATIC_SENTINEL_KEY : date
  // Wait for the week (it decides Static vs dated) so the audited roster read runs once, for the right day.
  const { data: available, isLoading: canvassersLoading } = useAvailableCanvassers(campaignId, week ? scheduledDate : undefined)
  const canvassers = available?.canvassers ?? []

  const headsUp = !isStatic && week && grantId ? sameDayHeadsUp(week.canvassers, grantId, date) : null

  const { mutate: saveWeek, isPending } = useSaveItineraryWeek(campaignId)

  const handleSave = () => {
    if (!route || !grantId || !week) return
    const existing = week.canvassers.find(c => c.grantId === grantId)?.dayAssignments[scheduledDate] ?? []
    saveWeek(
      {
        assignments: [{ scheduledDate, turfId: route.turfId, assignedGrantId: grantId, sortOrder: existing.length }],
        deletedAssignmentIds: [],
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['followUp', campaignId, 'routes'] })
          toaster.create({ title: `${route.label} assigned`, type: 'success', duration: 3000 })
          onClose()
        },
        onError: (err) => {
          toaster.create({ title: 'Could not assign route', description: extractApiMessage(err, 'Please try again.'), type: 'error', duration: 7000 })
        },
      },
    )
  }

  const title = `Assign ${route?.label ?? 'route'}`

  const body: ReactNode = (
    <VStack gap={4} align="stretch" py={2}>
      {!isStatic && (
        <Box>
          <Text asChild fontSize="xs" fontWeight="semibold" color="muted" mb={1} display="block">
            <label htmlFor="assign-route-date">Date</label>
          </Text>
          <Input id="assign-route-date" type="date" minH="44px" value={date} onChange={(e) => { if (e.target.value) setDate(e.target.value) }} />
        </Box>
      )}
      <Box>
        <Text fontSize="xs" fontWeight="semibold" color="muted" mb={1}>Canvasser</Text>
        {(!week || canvassersLoading) ? (
          <VStack py={4}><Spinner color="accent" /></VStack>
        ) : canvassers.length === 0 ? (
          <Text fontSize="sm" color="muted">No canvassers available{isStatic ? '' : ' that day'}.</Text>
        ) : (
          <VStack gap={1} align="stretch">
            {canvassers.map(c => {
              const selected = grantId === c.grantId
              return (
                <HStack
                  key={c.grantId}
                  p={2}
                  minH="48px"
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={selected ? 'accent' : 'border'}
                  bg={selected ? 'accent.soft' : 'surface'}
                  aria-pressed={selected}
                  aria-label={c.fullName}
                  asChild
                >
                  <button type="button" onClick={() => setGrantId(c.grantId)}>
                    <Avatar.Root size="xs"><Avatar.Fallback name={c.fullName} /><Avatar.Image src={c.portraitUrl ?? undefined} /></Avatar.Root>
                    <Text fontSize="sm">{c.fullName}</Text>
                  </button>
                </HStack>
              )
            })}
          </VStack>
        )}
      </Box>
      {headsUp && <Text fontSize="sm" color="info.fg" role="status">{headsUp}</Text>}
    </VStack>
  )

  const footer: ReactNode = (
    <HStack gap={3} justify="flex-end" w="100%">
      <Button variant="ghost" minH="44px" onClick={onClose}>Cancel</Button>
      <Button colorPalette="brand" minH="44px" loading={isPending} disabled={!grantId || !week || !route} onClick={handleSave}>
        Save
      </Button>
    </HStack>
  )

  if (isMobile) {
    return (
      <Drawer.Root open={isOpen} placement="bottom" onOpenChange={e => { if (!e.open) onClose() }}>
        <Portal>
          <Drawer.Backdrop />
          <Drawer.Positioner>
            <Drawer.Content borderTopRadius="xl" maxH="85vh" pb="env(safe-area-inset-bottom, 0px)">
              <Drawer.CloseTrigger asChild><CloseButton size="sm" /></Drawer.CloseTrigger>
              <Drawer.Header borderBottomWidth="1px">{title}</Drawer.Header>
              <Drawer.Body>{body}</Drawer.Body>
              <Drawer.Footer borderTopWidth="1px">{footer}</Drawer.Footer>
            </Drawer.Content>
          </Drawer.Positioner>
        </Portal>
      </Drawer.Root>
    )
  }

  return (
    <ResponsiveModal isOpen={isOpen} onClose={onClose} size="md">
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content>
          <Dialog.Header>{title}</Dialog.Header>
          <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
          <Dialog.Body>{body}</Dialog.Body>
          <Dialog.Footer>{footer}</Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </ResponsiveModal>
  )
}
